import React, { useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    ImageList,
    ImageListItem,
    Typography,
    styled
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { Label } from './SharedComponents';
import apiClient from '../services/apiClient';
import notify from '../services/notify';

interface ProductImageUploadProps {
    productId?: string;
    initialImages?: string[] | null;
    onChange: (images: string[]) => void;
}

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const ImageContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    height: '100%',
    '&:hover .deleteButton': {
        opacity: 1,
    },
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    color: 'white',
    padding: '4px',
    opacity: 0,
    transition: 'opacity 0.2s',
    '&:hover': {
        backgroundColor: 'rgba(255, 0, 0, 0.7)',
        opacity: 1,
    },
}));

const ImagePlaceholder = styled(Box)(({ theme }) => ({
    width: '100%',
    aspectRatio: '1', // Ensures a 1:1 aspect ratio for a square
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-start',
    border: '1px dashed #ccc',
    borderRadius: '4px',
    backgroundColor: '#f5f5f5',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: '#e0e0e0',
    },
}));

export default function ProductImageUpload({ productId, initialImages = [], onChange }: ProductImageUploadProps) {
    // Initialize with empty array if initialImages is null
    const [images, setImages] = useState<string[]>(initialImages || []);
    const [isUploading, setIsUploading] = useState<boolean>(false);

    // Update images when initialImages change
    React.useEffect(() => {
        console.log('ProductImageUpload - initialImages changed:', initialImages);
        setImages(initialImages || []);
    }, [initialImages]);

    // Handle file input change
    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files || !productId) return;

        const files = Array.from(event.target.files);

        // Check if adding new images will exceed the limit
        if (images.length + files.length > 5) {
            notify('error', `You can only upload a maximum of 5 images (${5 - images.length} remaining)`);
            return;
        }

        setIsUploading(true);

        try {
            const formData = new FormData();

            // Append each file to formData
            files.forEach(file => {
                formData.append('images', file);
            });

            // Upload images
            const response = await apiClient.post(`/uploads/product-images/${productId}`, formData);

            // Update state with new images
            const updatedImages = [...images, ...response.data.uploaded_images];
            setImages(updatedImages);
            onChange(updatedImages);

            notify('success', 'Images uploaded successfully');
        } catch (error) {
            notify('error', 'Failed to upload images');
            console.error('Error uploading images:', error);
        } finally {
            setIsUploading(false);
        }
    };

    // Handle image deletion
    const handleDeleteImage = async (index: number) => {
        if (!productId) return;

        try {
            // Delete image via API using our new delete method
            await apiClient.delete(`/uploads/product-images/${productId}/${index}`);

            // Update state by removing the deleted image
            const updatedImages = [...images];
            updatedImages.splice(index, 1);
            setImages(updatedImages);
            onChange(updatedImages);

            notify('success', 'Image deleted successfully');
        } catch (error) {
            notify('error', 'Failed to delete image');
            console.error('Error deleting image:', error);
        }
    };

    return (
        <Box sx={{ mt: 2, mb: 3 }}>
            <Label>Product Images (Max 5)</Label>
            <ImageList sx={{ width: '100%', height: 'auto', marginTop: '5px', overflowY: 'initial' }} cols={3} >
                {images.map((imageUrl, index) => (
                    <ImageListItem key={index}>
                        <ImageContainer>
                            <div
                                style={{
                                    width: '100%',
                                    paddingTop: '100%', // Maintains a square aspect ratio
                                    backgroundImage: `url(${imageUrl})`,
                                    backgroundSize: 'cover', // Ensures the image covers the entire div
                                    backgroundPosition: 'center', // Centers the image
                                    backgroundRepeat: 'no-repeat', // Prevents the image from repeating
                                }}
                                aria-label={`Product ${index}`} // For accessibility
                            ></div>
                            <DeleteButton
                                className="deleteButton"
                                size="small"
                                onClick={() => handleDeleteImage(index)}
                            >
                                <Delete fontSize="small" />
                            </DeleteButton>
                        </ImageContainer>
                    </ImageListItem>
                ))}

                {/* Add image placeholder if less than 5 images */}
                {images.length < 5 && (
                    <ImageListItem>
                        <Button
                            component="label"
                            disabled={isUploading || !productId}
                            sx={{ width: '100%', height: '100%', p: 0 }}
                        >
                            <ImagePlaceholder>
                                {isUploading ? (
                                    <CircularProgress size={24} />
                                ) : (
                                    <>
                                        <Add />
                                        <Typography variant="body2" sx={{ ml: 1 }}>
                                            Add Image
                                        </Typography>
                                    </>
                                )}
                            </ImagePlaceholder>
                            <VisuallyHiddenInput
                                type="file"
                                accept="image/jpeg,image/png,image/gif,image/webp"
                                onChange={handleFileChange}
                                multiple
                            />
                        </Button>
                    </ImageListItem>
                )}
            </ImageList>
            {!productId && (
                <Typography variant="caption" color="text.secondary">
                    Save product first to enable image upload
                </Typography>
            )}
        </Box>
    );
}